import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Chip
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import AttachmentIcon from '@mui/icons-material/Attachment';
import HighlightAltIcon from '@mui/icons-material/HighlightAlt';
import type { DriveResource, DriveResourceType, DriveSectionId } from '../types';
import { DRIVE_SECTION_LABEL_MAP } from '../constants';
import { extractGoogleDriveFileId } from '../utils/googleDrive';

export interface DriveResourceFormValues {
  title: string;
  description?: string;
  resourceType: DriveResourceType;
  externalUrl?: string;
  googleDriveId?: string;
  pinWeight?: string;
  tags?: string;
}

interface DriveResourceFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  sectionId: DriveSectionId;
  resource?: DriveResource;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (values: DriveResourceFormValues, file: File | null) => Promise<void>;
}

const DEFAULT_VALUES: DriveResourceFormValues = {
  title: '',
  description: '',
  resourceType: 'storage',
  externalUrl: '',
  googleDriveId: '',
  pinWeight: '0',
  tags: ''
};

export const DriveResourceFormDialog: React.FC<DriveResourceFormDialogProps> = ({
  open,
  mode,
  sectionId,
  resource,
  submitting,
  onClose,
  onSubmit
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm<DriveResourceFormValues>({
    defaultValues: DEFAULT_VALUES
  });

  const resourceType = watch('resourceType');
  const externalUrlValue = watch('externalUrl');
  const googleDriveIdValue = watch('googleDriveId');

  useEffect(() => {
    if (mode === 'edit' && resource) {
      reset({
        title: resource.title,
        description: resource.description || '',
        resourceType: resource.resourceType,
        externalUrl: resource.resourceType === 'storage' ? '' : resource.externalUrl || '',
        googleDriveId: resource.resourceType === 'google-drive' ? resource.googleDriveId || '' : '',
        pinWeight: resource.pinWeight !== undefined ? String(resource.pinWeight) : '0',
        tags: resource.tags?.join(', ') || ''
      });
      setSelectedFile(null);
    } else if (mode === 'create') {
      reset(DEFAULT_VALUES);
      setSelectedFile(null);
    }
  }, [mode, resource, reset]);

  useEffect(() => {
    if (resourceType !== 'google-drive') {
      return;
    }

    const derivedId = extractGoogleDriveFileId(externalUrlValue);
    if (derivedId && derivedId !== googleDriveIdValue) {
      setValue('googleDriveId', derivedId, { shouldValidate: true, shouldDirty: false });
    }
  }, [resourceType, externalUrlValue, googleDriveIdValue, setValue]);

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleFormSubmit = handleSubmit(async (values) => {
    const requiresFile = values.resourceType === 'storage' && mode === 'create';
    if (requiresFile && !selectedFile) {
      throw new Error('File selection is required for uploaded resources');
    }

    await onSubmit(values, selectedFile);
    setSelectedFile(null);
  });

  const formattedTags = resource?.tags || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {mode === 'create' ? 'Add Drive Resource' : `Edit ${resource?.title}`}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Section
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {DRIVE_SECTION_LABEL_MAP[sectionId]}
            </Typography>
          </Box>

          <Controller
            name="title"
            control={control}
            rules={{ required: 'Title is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Title"
                fullWidth
                required
                error={!!errors.title}
                helperText={errors.title?.message}
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                fullWidth
                multiline
                minRows={3}
                helperText="Optional context shown in the resource card"
              />
            )}
          />

          <FormControl component="fieldset" disabled={mode === 'edit'}>
            <FormLabel component="legend">Resource type</FormLabel>
            <Controller
              name="resourceType"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field} row>
                  <FormControlLabel value="storage" control={<Radio />} label="Upload file" />
                  <FormControlLabel value="external" control={<Radio />} label="External link" />
                  <FormControlLabel value="google-drive" control={<Radio />} label="Google Drive" />
                </RadioGroup>
              )}
            />
          </FormControl>

          {resourceType !== 'storage' && (
            <Controller
              name="externalUrl"
              control={control}
              rules={{
                required: 'URL is required',
                pattern: {
                  value: /^(https?:\/\/).+/i,
                  message: 'Provide a valid URL starting with http or https'
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Resource URL"
                  fullWidth
                  required
                  error={!!errors.externalUrl}
                  helperText={errors.externalUrl?.message || 'Paste the public or shared link to the resource'}
                />
              )}
            />
          )}

          {resourceType === 'google-drive' && (
            <Controller
              name="googleDriveId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Google Drive file ID"
                  fullWidth
                  helperText="Automatically detected from the URL when possible; override if needed."
                />
              )}
            />
          )}

          {resourceType === 'storage' && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Upload file
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachmentIcon />}
              >
                {selectedFile ? 'Change file' : 'Select file'}
                <input type="file" hidden onChange={handleFileInputChange} />
              </Button>
              {selectedFile ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                </Typography>
              ) : mode === 'edit' && resource?.resourceType === 'storage' ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Current file: {resource.fileName}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Attach PDFs, DOCX, spreadsheets, or other reference files.
                </Typography>
              )}
            </Box>
          )}

          <Controller
            name="pinWeight"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="number"
                label="Pin weight"
                fullWidth
                inputProps={{ min: 0 }}
                helperText="Higher numbers surface the resource towards the top within the section"
              />
            )}
          />

          <Controller
            name="tags"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Tags"
                fullWidth
                helperText="Optional: comma-separated tags to aid discovery"
              />
            )}
          />

          {mode === 'edit' && formattedTags.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {formattedTags.map((tag) => (
                <Chip key={tag} label={tag} size="small" icon={<HighlightAltIcon fontSize="small" />} />
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleFormSubmit} variant="contained" disabled={submitting}>
          {mode === 'create' ? 'Add resource' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriveResourceFormDialog;
